import React, { useState, forwardRef, useImperativeHandle } from "react";
import { MessageBar, Label, MessageBarType } from "@fluentui/react";
import { For } from "react-loops";

export interface INotificationMessagesState {
  errors: string[];
  info: string[];
  success: string[];
  warning: string[];
}

const initialState: INotificationMessagesState = {
  errors: [],
  info: [],
  success: [],
  warning: []
};

function getMessageBarType(type: keyof INotificationMessagesState) {
  switch (type) {
    case "errors":
      return MessageBarType.error;
    case "warning":
      return MessageBarType.warning;
    case "success":
      return MessageBarType.success;
    default:
      return MessageBarType.info;
  }
}

interface INotificationMessagesSetOptions {
  preventReset: boolean;
}

export interface INotificationMessagesRef {
  setMessage: (
    type: keyof INotificationMessagesState,
    messages: string[],
    options?: INotificationMessagesSetOptions
  ) => void;
  reset: () => void;
}

const NotificationMessages = forwardRef((props, ref: React.Ref<INotificationMessagesRef>) => {
  const [state, setState] = useState(initialState);

  useImperativeHandle(ref, () => ({
    setMessage(type, messages) {
      setState({ ...state, [type]: messages });
    },
    reset() {
      setState(initialState);
    }
  }));

  const clearType = (type: string) => {
    setState({ ...state, [type]: [] });
  };

  const getMessageBar = (type: keyof INotificationMessagesState) => {
    return state[type].length !== 0 ? (
      <MessageBar
        messageBarType={getMessageBarType(type)}
        onDismiss={() => clearType(type)}
        dismissButtonAriaLabel="Close"
        overflowButtonAriaLabel="See more"
      >
        <For of={state[type]}>{(item) => <Label>{item}</Label>}</For>
      </MessageBar>
    ) : null;
  };

  const errorMessages = getMessageBar("errors");
  const warningMessages = getMessageBar("warning");
  const infoMessages = getMessageBar("info");
  const successMessages = getMessageBar("success");
  return (
    <React.Fragment>
      {errorMessages}
      {warningMessages}
      {infoMessages}
      {successMessages}
    </React.Fragment>
  );
});

NotificationMessages.displayName = "NotificationMessages";

export default NotificationMessages;
